import type { ElementConfigOf } from "@p0security/shared/install/types";

import type { VaultElement } from "./components.ts";

export type SecretManagerLocator =
  | {
      provider: "aws";
      accountId: string;
      region: string;
    }
  | {
      provider: "azure";
      vaultName: string;
    }
  | {
      provider: "gcp";
      projectId: string;
    };

// Key Vault's rule for a secret name.
const KEY_VAULT_SECRET_NAME_MAX_LENGTH = 127;

/** A Secret Manager provider's naming policy. Secret names are generated in a
 * single canonical (AWS-style, slash-delimited) form; each provider's
 * `replacer` rewrites that canonical name into one its Secret Manager accepts. */
export type SecretProvider = {
  replacer: (canonicalName: string) => string;
};

const SECRET_PROVIDERS: Record<
  SecretManagerLocator["provider"],
  SecretProvider
> = {
  // AWS Secrets Manager permits `/` and `.`, so the canonical name is used verbatim.
  aws: { replacer: (name) => name },
  // GCP Secret Manager ids are limited to `[A-Za-z0-9_-]`. The path delimiter
  // (`/`) becomes `_`, which a GitHub organization name cannot hold, so the
  // organization keeps a boundary that an IAM condition can match exactly. A
  // dash would not: the name of organization "foo" would be a prefix of every
  // name of organization "foo-bar". Dots (e.g. the `.pem` suffix) become `-`.
  gcp: { replacer: (name) => name.replace(/\//g, "_").replace(/\./g, "-") },
  // Key Vault names hold only letters, digits and `-`. Mapping every other
  // character to `-` can merge two names, but generated names end in a hash of
  // their parts, which keeps them apart. Truncating could cut that hash off, so
  // an overlong name is refused instead.
  azure: {
    replacer: (name) => {
      const replaced = name.replace(/[^A-Za-z0-9-]/g, "-");
      if (replaced.length > KEY_VAULT_SECRET_NAME_MAX_LENGTH) {
        throw new Error(
          `The secret name '${replaced}' is longer than the ${KEY_VAULT_SECRET_NAME_MAX_LENGTH} characters Azure Key Vault allows`
        );
      }
      return replaced;
    },
  },
};

/** The {@link SecretProvider} for a locator's provider — its Secret-Manager naming policy. */
export const secretProviderFor = (
  provider: SecretManagerLocator["provider"]
): SecretProvider => SECRET_PROVIDERS[provider];

/** The URL a Key Vault in Azure's public cloud is served at. */
export const keyVaultUrl = (vaultName: string) =>
  `https://${vaultName}.vault.azure.net`;

type VaultConfig = ElementConfigOf<VaultElement>;
export type VaultType = VaultConfig["type"];
/** A {@link VaultElement}'s config for the vault type `K`. */
export type VaultConfigOf<K extends VaultType> = Extract<
  VaultConfig,
  { type: K }
>;

type VaultProviders = { "aws-sm": "aws"; "azure-kv": "azure"; "gcp-sm": "gcp" };

/** The provider of a vault of type `K`, e.g. "aws" for "aws-sm". */
export type VaultProviderOf<K extends VaultType> = VaultProviders[K];

/** The locator of a vault of type `K`. */
export type SecretManagerLocatorOf<K extends VaultType> = Extract<
  SecretManagerLocator,
  { provider: VaultProviderOf<K> }
>;

const VAULT_LOCATORS: {
  [K in VaultType]: (field: VaultConfigOf<K>) => SecretManagerLocatorOf<K>;
} = {
  "aws-sm": ({ install, secretsRegion }) => ({
    provider: "aws",
    accountId: install,
    region: secretsRegion,
  }),
  "azure-kv": ({ vaultName }) => ({ provider: "azure", vaultName }),
  "gcp-sm": ({ install }) => ({ provider: "gcp", projectId: install }),
};

/** Where a vault's secrets are. Takes any config that has a
 * {@link VaultElement}'s fields, such as a `SecretManagerElement`'s. The
 * locator's provider follows the config's type, so a config that can't be
 * Azure Key Vault gets a locator that can't be Azure. */
export const parseSecretManagerField = <K extends VaultType>(
  field: VaultConfigOf<K>
): SecretManagerLocatorOf<K> => VAULT_LOCATORS[field.type](field);
